import { constructValue } from '@adornis/baseql/entities/construct.js';
import { loadMongoData } from '@adornis/baseql/server/data-loader.js';
import { StudentType } from '@chancenwerk/enums/enums.js';
import { DateTime } from 'luxon';
import type { Product } from '../../../_cms/products/entities/Product.js';
import { ChancenRole } from '../../../semantics/ChancenRole.js';
import { ExternalKPFRelationship } from '../../../semantics/ExternalKPFRelationship.js';
import { StudentParticipationRelationship } from '../../../semantics/StudentParticipationRelationship.js';
import { Taetigkeit } from '../../../semantics/Taetigkeit.js';
import { ChancenwerkUser } from '../../users/entities/ChancenwerkUser.js';
import { Student } from '../../users/entities/Student.js';
import type { KPF } from '../entities/KPF.js';

export async function resolveKPFRelationshipsForRole<U extends ChancenwerkUser, T extends Product>(
  kpf: KPF<T>,
  predfinedPredicate: string,
  studentType: StudentType = StudentType.US,
): Promise<U[]> {
  const predicate = (await loadMongoData(ChancenRole, { _id: predfinedPredicate })).at(0);
  if (!predicate)
    throw new Error(
      'Tried to resolve relationship for KPF, but predicate ' + predfinedPredicate + ' was not found in the db.',
    );

  // get the incoming relationships of the kpf which are active
  const relationshipsForKPF = await loadMongoData(Taetigkeit, {
    objectID: kpf._id,
    predicateID: predicate._id,
    $or: [
      { $and: [{ _class: Taetigkeit._class }, Taetigkeit.isActiveMongoSelector()] },
      { _class: StudentParticipationRelationship._class },
      { _class: ExternalKPFRelationship._class },
    ],
  });

  const users = (
    await loadMongoData(
      ChancenwerkUser,
      relationshipsForKPF.map(rel => rel.subjectID),
    )
  ).map(user => constructValue(user) as U);

  // student do not have an active state on the relationship. They have on in their account
  return users.filter(u =>
    u instanceof Student
      ? u.schuelertyp === studentType &&
        u.joinInformation.eintrittsdatum &&
        u.joinInformation.eintrittsdatum?.startOf('day') <= DateTime.now() &&
        (!u.kuendigung.austrittsdatum || u.kuendigung.austrittsdatum.startOf('day') > DateTime.now())
      : true,
  );
}
